import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { observer } from "mobx-react";
import { Modal } from '@fluentui/react';
import FeedSubscribed from "./FeedSubscribed";
import discoverStore from "../store";
import userStore from "../../../Stores/User";
import { CircularProgress, Tooltip } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { addUserFeed, getUserFeeds } from '../../../audiblogsApi';
import gamificationStore from '../../../Stores/GamificationStore';

const FeedsModal = observer(({ isOpen, onClose }) => {
  const { 
    userFeeds, 
    allFeeds, 
    feedsLoading, 
    allFeedsLoading, 
    fetchUserFeeds, 
    fetchAllFeeds,
    unsubscribeFromFeed, 
    changeCurrentArticle, 
    currentArticle,
    search,
    pendingSearch,
    mode,
    changeMode
  } = discoverStore;
  
  const [error, setError] = useState(null);
  const [visibleUnsubscribedCount, setVisibleUnsubscribedCount] = useState(10);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [rssUrlInput, setRssUrlInput] = useState('');
  const [isAddingFeed, setIsAddingFeed] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const loaderRef = useRef(null);
  const rssInputRef = useRef(null);
  
  useEffect(() => {
    if (isOpen && userStore.idToken) {
      try {
        fetchUserFeeds();
        fetchAllFeeds();
      } catch (err) {
        console.error("Error fetching feeds:", err);
        setError("Failed to load your feeds. Please try again later.");
      }
    }
  }, [isOpen]);

  // Focus the RSS input when modal opens
  useEffect(() => {
    if (isOpen) {
      // Try multiple times with different delays to ensure focus works
      const focusAttempts = [100, 300, 500, 1000];
      focusAttempts.forEach(delay => {
        setTimeout(() => {
          if (rssInputRef.current) {
            rssInputRef.current.focus();
            console.log(`RSS input focused after ${delay}ms`);
          } else {
            console.log(`RSS input ref not found after ${delay}ms`);
          }
        }, delay);
      });
    }
  }, [isOpen]);

  // Memoize the filter functions
  const subscribedFeedIds = useMemo(() => 
    new Set((userFeeds || []).map(feed => feed.feed_id)), 
    [userFeeds]
  );
  
  const unsubscribedFeedsAll = useMemo(() => 
    (allFeeds || []).filter(feed => !subscribedFeedIds.has(feed.feed_id)),
    [allFeeds, subscribedFeedIds]
  );

  const searchTerm = useMemo(() => 
    searchInput.toLowerCase().trim(),
    [searchInput]
  );
  
  const filteredUserFeeds = useMemo(() => 
    searchTerm 
      ? (userFeeds || []).filter(feed => 
          feed.title?.toLowerCase().includes(searchTerm) || 
          feed.description?.toLowerCase().includes(searchTerm) ||
          feed.author?.toLowerCase().includes(searchTerm) ||
          feed.url?.toLowerCase().includes(searchTerm)
        )
      : userFeeds,
    [userFeeds, searchTerm]
  );

  const filteredUnsubscribedFeeds = useMemo(() => 
    searchTerm 
      ? unsubscribedFeedsAll.filter(feed => 
          feed.title?.toLowerCase().includes(searchTerm) || 
          feed.description?.toLowerCase().includes(searchTerm) ||
          feed.author?.toLowerCase().includes(searchTerm) ||
          feed.url?.toLowerCase().includes(searchTerm)
        )
      : unsubscribedFeedsAll,
    [unsubscribedFeedsAll, searchTerm]
  );

  // Define feed categories based on content analysis
  const feedCategories = useMemo(() => {
    const categories = {
      all: { label: 'All Feeds', count: (allFeeds || []).length },
      tech: { label: 'Technology', count: 0 },
      news: { label: 'News & Politics', count: 0 },
      business: { label: 'Business & Finance', count: 0 },
      science: { label: 'Science & Research', count: 0 },
      culture: { label: 'Culture & Arts', count: 0 }
    };

    // Count feeds in each category
    (allFeeds || []).forEach(feed => {
      const title = (feed.title || '').toLowerCase();
      const desc = (feed.description || '').toLowerCase();
      const url = (feed.feed_id || '').toLowerCase();

      if (title.includes('tech') || desc.includes('tech') || url.includes('tech') ||
          title.includes('software') || desc.includes('software') ||
          title.includes('programming') || desc.includes('programming') ||
          title.includes('ai') || desc.includes('ai') ||
          title.includes('artificial intelligence') || desc.includes('artificial intelligence')) {
        categories.tech.count++;
      }
      if (title.includes('news') || desc.includes('news') || url.includes('news') ||
          title.includes('politics') || desc.includes('politics') ||
          title.includes('current events') || desc.includes('current events')) {
        categories.news.count++;
      }
      if (title.includes('business') || desc.includes('business') || url.includes('business') ||
          title.includes('finance') || desc.includes('finance') ||
          title.includes('economy') || desc.includes('economy') ||
          title.includes('market') || desc.includes('market')) {
        categories.business.count++;
      }
      if (title.includes('science') || desc.includes('science') || url.includes('science') ||
          title.includes('research') || desc.includes('research') ||
          title.includes('health') || desc.includes('health') ||
          title.includes('medicine') || desc.includes('medicine')) {
        categories.science.count++;
      }
      if (title.includes('culture') || desc.includes('culture') || url.includes('culture') ||
          title.includes('art') || desc.includes('art') ||
          title.includes('music') || desc.includes('music') ||
          title.includes('literature') || desc.includes('literature') ||
          title.includes('film') || desc.includes('film')) {
        categories.culture.count++;
      }
    });

    return categories;
  }, [allFeeds]);

  // Filter feeds based on selected category
  const filterFeedsByCategory = useCallback((feeds) => {
    if (selectedCategory === 'all') return feeds;

    return feeds.filter(feed => {
      const title = (feed.title || '').toLowerCase();
      const desc = (feed.description || '').toLowerCase();
      const url = (feed.feed_id || '').toLowerCase();

      switch (selectedCategory) {
        case 'tech':
          return title.includes('tech') || desc.includes('tech') || url.includes('tech') ||
                 title.includes('software') || desc.includes('software') ||
                 title.includes('programming') || desc.includes('programming') ||
                 title.includes('ai') || desc.includes('ai') ||
                 title.includes('artificial intelligence') || desc.includes('artificial intelligence');
        case 'news':
          return title.includes('news') || desc.includes('news') || url.includes('news') ||
                 title.includes('politics') || desc.includes('politics') ||
                 title.includes('current events') || desc.includes('current events');
        case 'business':
          return title.includes('business') || desc.includes('business') || url.includes('business') ||
                 title.includes('finance') || desc.includes('finance') ||
                 title.includes('economy') || desc.includes('economy') ||
                 title.includes('market') || desc.includes('market');
        case 'science':
          return title.includes('science') || desc.includes('science') || url.includes('science') ||
                 title.includes('research') || desc.includes('research') ||
                 title.includes('health') || desc.includes('health') ||
                 title.includes('medicine') || desc.includes('medicine');
        case 'culture':
          return title.includes('culture') || desc.includes('culture') || url.includes('culture') ||
                 title.includes('art') || desc.includes('art') ||
                 title.includes('music') || desc.includes('music') ||
                 title.includes('literature') || desc.includes('literature') ||
                 title.includes('film') || desc.includes('film');
        default:
          return true;
      }
    });
  }, [selectedCategory]);

  // Apply category filter to both subscribed and unsubscribed feeds
  const filteredUserFeedsWithCategory = useMemo(() => 
    filterFeedsByCategory(filteredUserFeeds),
    [filterFeedsByCategory, filteredUserFeeds]
  );

  const filteredUnsubscribedFeedsWithCategory = useMemo(() => 
    filterFeedsByCategory(filteredUnsubscribedFeeds),
    [filterFeedsByCategory, filteredUnsubscribedFeeds]
  );

  const handleUnsubscribe = useCallback((feedId) => {
    try {
      unsubscribeFromFeed(feedId);
    } catch (err) {
      console.error("Error unsubscribing from feed:", err);
    }
  }, [unsubscribeFromFeed]);

  const handleSubscribe = useCallback((feedId) => {
    try {
      const feed = allFeeds.find(f => f.feed_id === feedId);
      if (feed) {
        discoverStore.subscribeToFeed(feed);
      }
    } catch (err) {
      console.error("Error subscribing to feed:", err);
    }
  }, [allFeeds]);

  const handleAddRssFeed = async (e) => {
    e.preventDefault();
    if (!rssUrlInput.trim()) return;

    setIsAddingFeed(true);
    try {
      // Check if user is already subscribed to this feed
      const userFeedsResponse = await getUserFeeds(userStore.idToken);
      const userFeeds = userFeedsResponse.data.feeds || [];
      const isAlreadySubscribed = userFeeds.some(feed => 
        feed.feed_url === rssUrlInput || 
        feed.feed_url.replace(/\/+$/, '') === rssUrlInput.replace(/\/+$/, '')
      );
      
      if (isAlreadySubscribed) {
        alert("You are already subscribed to this feed.");
        setIsAddingFeed(false);
        return;
      }
      
      // Add the feed
      await addUserFeed(userStore.idToken, rssUrlInput);
      
      // Award bonus characters for subscribing to feed
      gamificationStore.completeAction(gamificationStore.actions.SUBSCRIBE_FEED);
      
      // Refresh feeds
      await fetchUserFeeds();
      await fetchAllFeeds();
      
      // Clear input and show success
      setRssUrlInput('');
      alert("Feed added successfully!");
    } catch (err) {
      console.error("Error adding feed:", err);
      alert(err.response?.data?.message || "There was a problem adding your feed. Please check the URL and try again.");
    } finally {
      setIsAddingFeed(false);
    }
  };

  const loadMoreUnsubscribedFeeds = useCallback(() => {
    if (!isLoadingMore && filteredUnsubscribedFeedsWithCategory.length > visibleUnsubscribedCount) {
      setIsLoadingMore(true);
      setTimeout(() => {
        setVisibleUnsubscribedCount(prev => prev + 10);
        setIsLoadingMore(false);
      }, 300);
    }
  }, [isLoadingMore, visibleUnsubscribedCount, filteredUnsubscribedFeedsWithCategory]);

  // Reset visible count when search changes
  useEffect(() => {
    setVisibleUnsubscribedCount(10);
  }, [searchTerm]);

  // Set up the intersection observer for infinite scroll
  useEffect(() => {
    if (filteredUnsubscribedFeedsWithCategory.length <= visibleUnsubscribedCount || !loaderRef.current) {
      return;
    }

    const options = {
      root: null,
      rootMargin: '200px 0px',
      threshold: 0.1,
    };

    const handleObserver = (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        loadMoreUnsubscribedFeeds();
      }
    };

    const observer = new IntersectionObserver(handleObserver, options);
    observer.observe(loaderRef.current);

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [loadMoreUnsubscribedFeeds, filteredUnsubscribedFeedsWithCategory.length, visibleUnsubscribedCount]);

  if (error) {
    return (
      <Modal
        isOpen={isOpen}
        onDismiss={onClose}
        isBlocking={false}
        isDarkOverlay={true}
        containerClassName="feeds-modal-container"
      >
        <div style={{ textAlign: "center", padding: "20px", color: "#f44336" }}>
          <p>{error}</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onClose}
      isBlocking={false}
      isDarkOverlay={true}
      containerClassName="feeds-modal-container"
    >
      <div className="feeds-modal-content">
        {/* Header */}
        <div className="feeds-modal-header">
          <div className="feeds-modal-title">
            <RssFeedIcon style={{ fontSize: '28px', color: '#4292fa', marginRight: '12px' }} />
            <h1>RSS Feeds</h1>
          </div>
          <button 
            onClick={onClose}
            className="feeds-modal-close"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="feeds-scrollable-content">
          {/* RSS Feed URL Input */}
          <div className="rss-url-section">
            <div className="rss-url-header">
              <h2>Add RSS Feed by URL</h2>
              <Tooltip title="Learn how feeds work">
                <a 
                  href="https://help.audioread.com/en/articles/11060313-how-to-use-feeds-on-audioread"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="help-link"
                >
                  <HelpIcon />
                </a>
              </Tooltip>
            </div>
            <form onSubmit={handleAddRssFeed} className="rss-url-form">
              <div className="rss-url-input-container">
                <input
                  ref={rssInputRef}
                  type="url"
                  placeholder="Enter RSS Feed URL"
                  value={rssUrlInput}
                  onChange={(e) => setRssUrlInput(e.target.value)}
                  className="rss-url-input"
                  disabled={isAddingFeed}
                />
                <button
                  type="submit"
                  className="rss-url-submit"
                  disabled={isAddingFeed || !rssUrlInput.trim()}
                >
                  {isAddingFeed ? (
                    <CircularProgress size={20} style={{ color: 'white' }} />
                  ) : (
                    'Add Feed'
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Search Bar */}
          <div className="search-section">
            <div className="search-container">
              <SearchIcon className="search-icon" />
              <input
                type="text"
                placeholder="Search existing feeds..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="category-filter">
            {Object.entries(feedCategories).map(([key, { label, count }]) => (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`category-button ${selectedCategory === key ? 'active' : ''}`}
              >
                {label}
                <span className="category-count">{count}</span>
              </button>
            ))}
          </div>

          {/* Loading State */}
          {feedsLoading || allFeedsLoading ? (
            <div className="feeds-loading">
              <CircularProgress 
                size={40} 
                style={{
                  color: '#4292fa',
                  '@keyframes gradient': {
                    '0%': { color: '#4292fa' },
                    '25%': { color: '#8535cd' },
                    '50%': { color: '#e82186' },
                    '75%': { color: '#ff5c57' },
                    '100%': { color: '#4292fa' }
                },
                animation: 'gradient 2s ease infinite'
              }}
            />
            <p>Loading feeds...</p>
          </div>
        ) : (
          <>
            {/* Subscribed Feeds Section */}
            {filteredUserFeedsWithCategory && filteredUserFeedsWithCategory.length > 0 && (
              <div className="feeds-section">
                <div className="section-header">
                  <div className="divider-line"></div>
                  <h2>My Feeds {searchTerm && `(${filteredUserFeedsWithCategory.length} results)`}</h2>
                  <div className="divider-line"></div>
                </div>
                <div className="feeds-grid">
                  {filteredUserFeedsWithCategory.map((feed) => (
                    <FeedSubscribed
                      key={feed.feed_id || feed.id || Math.random().toString()}
                      feed={feed}
                      idToken={userStore.idToken}
                      selectArticle={changeCurrentArticle}
                      isActive={currentArticle && currentArticle.article_id === feed.article_id}
                      onUnsubscribe={handleUnsubscribe}
                      isSubscribed={true}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* All Feeds Section */}
            {filteredUnsubscribedFeedsWithCategory && filteredUnsubscribedFeedsWithCategory.length > 0 && (
              <div className="feeds-section">
                <div className="section-header">
                  <div className="divider-line"></div>
                  <h2>All Feeds {searchTerm && `(${filteredUnsubscribedFeedsWithCategory.length} results)`}</h2>
                  <div className="divider-line"></div>
                </div>
                <div className="feeds-grid">
                  {filteredUnsubscribedFeedsWithCategory.slice(0, visibleUnsubscribedCount).map((feed) => (
                    <FeedSubscribed
                      key={feed.feed_id || feed.id || Math.random().toString()}
                      feed={feed}
                      idToken={userStore.idToken}
                      selectArticle={changeCurrentArticle}
                      isActive={currentArticle && currentArticle.article_id === feed.article_id}
                      onUnsubscribe={handleSubscribe}
                      isSubscribed={false}
                    />
                  ))}
                </div>
                
                {/* Infinite Scroll Loader */}
                {filteredUnsubscribedFeedsWithCategory.length > visibleUnsubscribedCount && (
                  <div 
                    ref={loaderRef} 
                    className="infinite-scroll-loader"
                  >
                    <CircularProgress 
                      size={24} 
                      style={{ color: 'rgba(66, 146, 250, 0.7)' }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* No Results */}
            {searchTerm && filteredUserFeedsWithCategory.length === 0 && filteredUnsubscribedFeedsWithCategory.length === 0 && (
              <div className="no-results">
                <p>No feeds found matching "{searchTerm}"</p>
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </Modal>
  );
});

export default FeedsModal; 